import { DOCUMENT } from '@angular/common';
import { Injectable, RendererFactory2, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import type { JsonLdEntry, SeoConfig } from '../models/seo.types';
import {
  SEO_DEFAULT_OG_IMAGE_ALT,
  SEO_DEFAULT_OG_IMAGE_HEIGHT,
  SEO_DEFAULT_OG_IMAGE_WIDTH,
  SEO_DEFAULT_ROBOTS,
  SEO_JSONLD_ID_PREFIX,
  SEO_NOINDEX_ROBOTS,
  SEO_SITE_JSONLD_ORG_ID,
  SEO_SITE_JSONLD_WEBSITE_ID,
  SEO_TITLE_SEPARATOR,
  SEO_TITLE_SUFFIX,
} from '../constants/seo.constants';
import { CountryContext } from '../country/country-context.service';

/**
 * Centralized SEO service.
 *
 * Manages `<title>`, `<meta>`, `<link rel="canonical">`, and
 * `<script type="application/ld+json">` elements for every page.
 *
 * Usage: call `setPage(config)` on every route change.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(RendererFactory2).createRenderer(null, null);
  private readonly countryContext = inject(CountryContext);

  /** Absolute site origin for the current market, without trailing slash (e.g. https://www.aposmart.se). */
  get baseUrl(): string {
    return this.countryContext.config.host.replace(/\/$/, '');
  }

  private get ogLocale(): string {
    // og:locale wants ll_CC; config.locale is the bare language, country supplies the region
    return `${this.countryContext.config.locale}_${this.countryContext.country}`;
  }

  /** IDs of JSON-LD scripts injected during the current page. */
  private readonly activeJsonLdIds = new Set<string>();

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Set all SEO tags for the current page.
   *
   * Call this once per route change. It replaces every tag managed
   * by this service; stale tags from the previous page are cleaned up.
   */
  setPage(config: SeoConfig): void {
    const fullTitle = config.noSuffix ? config.title : this.formatTitle(config.title);
    this.title.setTitle(fullTitle);

    // Basic meta
    this.meta.updateTag({ name: 'description', content: config.description });
    this.setRobots(config);

    // Open Graph
    this.setOpenGraph(config, fullTitle);

    // Twitter
    this.setTwitter(config, fullTitle);

    // Canonical
    this.setCanonical(config.canonicalPath);

    // Remove stale meta keywords (if leftover from pre-migration code)
    this.meta.removeTag('name="keywords"');

    // JSON-LD
    this.syncJsonLd(config.jsonLd ?? []);
  }

  /** Upsert a single JSON-LD script element by id. */
  setJsonLd(id: string, schema: Record<string, unknown>): void {
    const domId = SEO_JSONLD_ID_PREFIX + id;
    let script = this.document.getElementById(domId) as HTMLScriptElement | null;

    if (script) {
      script.textContent = JSON.stringify(schema);
    } else {
      script = this.renderer.createElement('script') as HTMLScriptElement;
      this.renderer.setAttribute(script, 'type', 'application/ld+json');
      this.renderer.setAttribute(script, 'id', domId);
      script.textContent = JSON.stringify(schema);
      this.renderer.appendChild(this.document.head, script);
    }

    this.activeJsonLdIds.add(domId);
  }

  /** Remove a single JSON-LD script element by id. */
  removeJsonLd(id: string): void {
    const domId = SEO_JSONLD_ID_PREFIX + id;
    const script = this.document.getElementById(domId);
    if (script) {
      this.renderer.removeChild(this.document.head, script);
    }
    this.activeJsonLdIds.delete(domId);
  }

  /** Remove all dynamically injected JSON-LD scripts. */
  removeAllJsonLd(): void {
    for (const domId of this.activeJsonLdIds) {
      const script = this.document.getElementById(domId);
      if (script) {
        this.renderer.removeChild(this.document.head, script);
      }
    }
    this.activeJsonLdIds.clear();
  }

  /**
   * Inject global site-level JSON-LD (Organization + WebSite) derived from
   * CountryContext. Uses a separate id namespace so syncJsonLd never removes
   * these on page navigation.
   *
   * Idempotent: safe to call on every render (SSR + client hydration).
   */
  setSiteJsonLd(): void {
    const { host, siteSchema } = this.countryContext.config;

    const orgSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      url: host,
      logo: `${this.baseUrl}/assets/logo_aposmart_lower.svg`,
      name: siteSchema.organizationName,
      areaServed: siteSchema.areaServed,
      sameAs: siteSchema.sameAs,
    };
    this.upsertSiteScript(SEO_SITE_JSONLD_ORG_ID, orgSchema);

    const websiteSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: host,
      name: 'Aposmart',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${this.baseUrl}/search/{search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    };
    this.upsertSiteScript(SEO_SITE_JSONLD_WEBSITE_ID, websiteSchema);
  }

  // ---------------------------------------------------------------------------
  // Title
  // ---------------------------------------------------------------------------

  /**
   * Append the brand suffix to a page title.
   *
   * The suffix is always appended so that brand identity is preserved
   * on social-share surfaces (og:title, twitter:title). Google may rewrite
   * long titles for display, but the full string is still used for ranking.
   */
  formatTitle(rawTitle: string): string {
    return rawTitle + SEO_TITLE_SEPARATOR + SEO_TITLE_SUFFIX;
  }

  // ---------------------------------------------------------------------------
  // Canonical
  // ---------------------------------------------------------------------------

  /**
   * Create or update `<link rel="canonical">`.
   *
   * Uses Renderer2 (not Meta service) because canonical is a `<link>`
   * element, not a `<meta>` element.
   */
  setCanonical(path?: string): void {
    const href = path ? this.baseUrl + path : this.baseUrl + this.cleanPath(this.router.url);

    const existing = this.document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (existing) {
      this.renderer.setAttribute(existing, 'href', href);
    } else {
      const link = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.setAttribute(link, 'href', href);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private upsertSiteScript(domId: string, schema: Record<string, unknown>): void {
    let script = this.document.getElementById(domId) as HTMLScriptElement | null;
    if (script) {
      script.textContent = JSON.stringify(schema);
    } else {
      script = this.renderer.createElement('script') as HTMLScriptElement;
      this.renderer.setAttribute(script, 'type', 'application/ld+json');
      this.renderer.setAttribute(script, 'id', domId);
      script.textContent = JSON.stringify(schema);
      this.renderer.appendChild(this.document.head, script);
    }
  }

  private setRobots(config: SeoConfig): void {
    const content = config.noindex ? SEO_NOINDEX_ROBOTS : (config.robots ?? SEO_DEFAULT_ROBOTS);

    this.meta.updateTag({ name: 'robots', content });
  }

  private setOpenGraph(config: SeoConfig, fullTitle: string): void {
    const ogImage = config.ogImage ?? `${this.baseUrl}/assets/og-image.png`;
    const canonicalUrl = config.canonicalPath
      ? this.baseUrl + config.canonicalPath
      : this.baseUrl + this.cleanPath(this.router.url);

    this.meta.updateTag({ property: 'og:title', content: config.ogTitle ?? fullTitle });
    this.meta.updateTag({
      property: 'og:description',
      content: config.ogDescription ?? config.description,
    });
    this.meta.updateTag({ property: 'og:type', content: config.ogType ?? 'website' });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: ogImage });
    this.meta.updateTag({ property: 'og:site_name', content: 'Aposmart' });
    this.meta.updateTag({ property: 'og:locale', content: this.ogLocale });

    // Only set image dimensions when we have real values
    if (config.ogImage) {
      const width = config.ogImageWidth ?? SEO_DEFAULT_OG_IMAGE_WIDTH;
      const height = config.ogImageHeight ?? SEO_DEFAULT_OG_IMAGE_HEIGHT;
      this.meta.updateTag({ property: 'og:image:width', content: width });
      this.meta.updateTag({ property: 'og:image:height', content: height });
      this.meta.updateTag({
        property: 'og:image:alt',
        content: config.ogImageAlt ?? fullTitle,
      });
    } else {
      // Default OG image -- use known dimensions
      this.meta.updateTag({ property: 'og:image:width', content: SEO_DEFAULT_OG_IMAGE_WIDTH });
      this.meta.updateTag({ property: 'og:image:height', content: SEO_DEFAULT_OG_IMAGE_HEIGHT });
      this.meta.updateTag({
        property: 'og:image:alt',
        content: config.ogImageAlt ?? SEO_DEFAULT_OG_IMAGE_ALT,
      });
    }
  }

  private setTwitter(config: SeoConfig, fullTitle: string): void {
    this.meta.updateTag({
      name: 'twitter:card',
      content: config.twitterCard ?? 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: config.ogTitle ?? fullTitle });
    this.meta.updateTag({
      name: 'twitter:description',
      content: config.ogDescription ?? config.description,
    });
    this.meta.updateTag({
      name: 'twitter:image',
      content: config.ogImage ?? `${this.baseUrl}/assets/og-image.png`,
    });
    this.meta.updateTag({
      name: 'twitter:image:alt',
      content: config.ogImage
        ? (config.ogImageAlt ?? fullTitle)
        : (config.ogImageAlt ?? SEO_DEFAULT_OG_IMAGE_ALT),
    });
  }

  /**
   * Sync JSON-LD scripts: add/update those in `entries`, remove any
   * that were active previously but are no longer in the list.
   */
  private syncJsonLd(entries: readonly JsonLdEntry[]): void {
    // Snapshot previous IDs before setJsonLd mutates the set
    const previousIds = new Set(this.activeJsonLdIds);
    const newIds = new Set<string>();

    for (const entry of entries) {
      this.setJsonLd(entry.id, entry.schema);
      newIds.add(SEO_JSONLD_ID_PREFIX + entry.id);
    }

    // Remove scripts that were active last page but not in this page's config
    for (const oldId of previousIds) {
      if (!newIds.has(oldId)) {
        const script = this.document.getElementById(oldId);
        if (script) {
          this.renderer.removeChild(this.document.head, script);
        }
      }
    }

    this.activeJsonLdIds.clear();
    for (const id of newIds) {
      this.activeJsonLdIds.add(id);
    }
  }

  /** Strip query params and fragments from a URL path. */
  private cleanPath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }
}
